import React, { useEffect, useMemo, useRef, useState } from "react";
import { Responsive } from "react-grid-layout";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    getDefaultDashboardWidgetDetailsAsync, getWidgetDataAsync, updateDashboardLayoutAsync,
    deleteAccountEmployeeDashboardWidgetAsync, getSystemDashboardWidgetsAsync,
    updateAccountEmployeeDashboardId, updateRefreshState, getAccountEmployeeDashboardsAsync,
    GetWidgetFilters, clearDashboardWidgets,
    getPagePermissions,
    renameWidgetAsync,
    getOrCreateDashboardAction, updateEmployeeOpenNewTabAction
} from "./dashboardSlice";
import DynamicWidget from "./DynamicWidget";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { getValueFromResource } from "../../common/timeLiveFunction";
import { t } from "i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddWidgetsModal from "./AddWidgetsModal";
import { ShowSwalWithConfirmation } from "../../common/SweetAlert";
import { ShowToast } from "../../common/Toastr";
import Loader from "../../common/Loader";
import { Tooltip as ReactTooltip } from "react-tooltip";
import WidgetFilterPopover from "./WidgetFilterPopover";
import { UpdateStateOpenInNewTab } from "../../common/GlobalSlice";

const RGL = Responsive as any;

const widgetStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: "12px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    height: "100%",
};

const headerStyle: React.CSSProperties = {
    margin: 0,
    marginBottom: 8,
    fontSize: 14,
    color: "#1a1a1b",
};

const descriptionStyle: React.CSSProperties = {
    fontSize: 12,
    color: "#9CA3AF",
    marginTop: -12,
    marginBottom: 10,
    fontWeight: 400,
};




const getStorageKey = (dashboardId: any) => `widgetFilters_${dashboardId}`;

const readFiltersFromStorage = (dashboardId: any): Record<string, any> => {
    if (!dashboardId) return {};
    try {
        const stored = localStorage.getItem(getStorageKey(dashboardId));
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

const writeFiltersToStorage = (dashboardId: any, filters: Record<string, any>) => {
    if (!dashboardId) return;
    try {
        localStorage.setItem(getStorageKey(dashboardId), JSON.stringify(filters));
    } catch { }
};



const DashboardView = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { AccountEmployeeDashboardId, OpenInNewTab } = useAppSelector((state) => state.globalState);
    const { dashboardWidgetDetails, widgetsResponseById, isLoading, refreshFalg } = useAppSelector((state) => state.dashboard);
    // const locationDashboardId = location.state?.obj?.AccountEmployeeDashboardsId ?? null;
    // const isDefaultDashboard = locationDashboardId === null;
    //const currentDashboardId = isDefaultDashboard ? AccountEmployeeDashboardId : locationDashboardId;

    const { id: urlDashboardId } = useParams<{ id: string }>();
    const locationDashboardId = location.state?.obj?.AccountEmployeeDashboardsId ?? null;
    const parsedUrlId = urlDashboardId ? parseInt(urlDashboardId, 10) : null;
    const isDefaultDashboard = locationDashboardId === null && parsedUrlId === null;
    const currentDashboardId = locationDashboardId ?? parsedUrlId ?? AccountEmployeeDashboardId;



    const [widgetFilters, setWidgetFilters] = useState<Record<string, any>>(
        () => readFiltersFromStorage(currentDashboardId)
    );
    const dashboardRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const [isDraggable, setIsDraggable] = useState(false);
    const [hoveredWidgetId, setHoveredWidgetId] = useState<string | null>(null);
    const [baseLayout, setBaseLayout] = useState<any[]>([]);
    const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
    const [filterPopover, setFilterPopover] = useState<{ widgetId: string; anchorEl: HTMLElement; widget: any } | null>(null);
    const [pinData, setPinData] = useState([]);
    const [editingWidgetId, setEditingWidgetId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState<string>("");
    const editInputRef = useRef<HTMLInputElement>(null);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [permissions, setPermissions] = useState({
        IsList: false,
        IsAdd: false,
        IsEdit: false,
        IsDelete: false,
    });
    const DashboardTitle = dashboardWidgetDetails?.[0]?.DashboardName;

    let selectedYear = new Date().getFullYear();
    const storageKey = `widgetFilters_${currentDashboardId}`;

    const storedValue = localStorage.getItem(storageKey);
    if (storedValue) {
        const parsed = JSON.parse(storedValue);
        Object.values(parsed).forEach((item: any) => {
            if (item?.YearsId?.length > 0) {
                selectedYear = item.YearsId[0];
            }
        });
    }

    const isFullPageLoading = useMemo(() => {
        if (isLoading) return true;

        if (dashboardWidgetDetails === null || dashboardWidgetDetails.length === 0) {
            return false;
        }

        const isAnyWidgetStillLoading = dashboardWidgetDetails.some((widget: any) => {
            const id = widget.AccountEmployeeDashboardDetailId;
            const status = widgetsResponseById[id];
            return !status || status.loading === true;
        });

        if (isAnyWidgetStillLoading) return true;
        if (width <= 0) return true;

        return false;
    }, [isLoading, dashboardWidgetDetails, widgetsResponseById, width]);

    const getPermission = async () => {
        try {
            const res = await dispatch(getPagePermissions(25));
            if (res.meta?.requestStatus === 'fulfilled') {
                if (!res?.payload?.IsList) {
                    navigate('../error/403');
                    return;
                }
                setPermissions({
                    IsList: res?.payload?.IsList ?? false,
                    IsAdd: res?.payload?.IsAdd ?? false,
                    IsEdit: res?.payload?.IsEdit ?? false,
                    IsDelete: res?.payload?.IsDelete ?? false,
                });
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getPermission();
        dispatch(getOrCreateDashboardAction());
    }, [])

    useEffect(() => {
        writeFiltersToStorage(currentDashboardId, widgetFilters);
    }, [widgetFilters, currentDashboardId]);

    useEffect(() => {
        setWidgetFilters(readFiltersFromStorage(currentDashboardId));
    }, [currentDashboardId]);

    useEffect(() => {
        if (!currentDashboardId) return;
        setIsDraggable(false);
        setBaseLayout([]);
        dispatch(clearDashboardWidgets());
        dispatch(getSystemDashboardWidgetsAsync());
        dispatch(getDefaultDashboardWidgetDetailsAsync(currentDashboardId));
        dispatch(updateAccountEmployeeDashboardId(currentDashboardId));
    }, [dispatch, currentDashboardId, refreshFalg]);

    useEffect(() => {
        const fetchPinData = async () => {
            try {
                const response = await dispatch(getAccountEmployeeDashboardsAsync());
                const data = response?.payload?.Data;
                setPinData(data?.filter((item: any) => item?.IsFavourite === true) ?? []);
            } catch (error) {
                console.log(error);
            }
        };
        fetchPinData();
    }, [dispatch, refreshFalg]);

    useEffect(() => {
        if (!dashboardWidgetDetails?.length) return;
        dashboardWidgetDetails.forEach((widget: any) => {
            const widgetId = widget.AccountEmployeeDashboardDetailId;
            const savedFilter = widgetFilters[widgetId];
            dispatch(getWidgetDataAsync({
                AccountEmployeeDashboardDetailId: widgetId,
                ...(savedFilter && Object.keys(savedFilter).length > 0
                    ? { filterJson: JSON.stringify(savedFilter) }
                    : {}),
            }));
        });
        dispatch(updateRefreshState(false));
    }, [dashboardWidgetDetails, dispatch, refreshFalg]);

    useEffect(() => {
        if (!dashboardWidgetDetails?.length || !currentDashboardId) return;

        const firstWidget = dashboardWidgetDetails[0];
        if (
            firstWidget?.AccountEmployeeDashboardsId &&
            firstWidget.AccountEmployeeDashboardsId !== currentDashboardId
        ) return;

        let existingLayoutArray: any[] = [];
        try {
            existingLayoutArray = firstWidget.Layout ? JSON.parse(firstWidget.Layout) : [];
        } catch {
            existingLayoutArray = [];
        }

        const validIds = new Set(
            dashboardWidgetDetails.map((w: any) => w.AccountEmployeeDashboardDetailId.toString())
        );

        const cleanedExistingLayout = existingLayoutArray.filter((item: any) =>
            validIds.has(item.i)
        );

        const missingWidgets = dashboardWidgetDetails.filter((widget: any) =>
            !cleanedExistingLayout.some(
                (item: any) => item.i === widget.AccountEmployeeDashboardDetailId.toString()
            )
        );

        let finalLayout = [...cleanedExistingLayout];

        if (missingWidgets.length > 0) {
            const COLS = 12;

            const getMaxRow = (layout: any[]) =>
                layout.reduce((max, item) => Math.max(max, (item.y ?? 0) + (item.h ?? 0)), 0);

            const buildOccupancy = (layout: any[]): Set<string> => {
                const occupied = new Set<string>();
                layout.forEach(({ x, y, w, h }) => {
                    for (let row = y; row < y + h; row++) {
                        for (let col = x; col < x + w; col++) {
                            occupied.add(`${row},${col}`);
                        }
                    }
                });
                return occupied;
            };

            const findFirstFit = (
                occupied: Set<string>,
                widgetW: number,
                widgetH: number,
                maxUsedRow: number
            ): { x: number; y: number } => {
                for (let row = 0; row < maxUsedRow; row++) {
                    for (let col = 0; col <= COLS - widgetW; col++) {
                        let fits = true;
                        outer: for (let dr = 0; dr < widgetH; dr++) {
                            for (let dc = 0; dc < widgetW; dc++) {
                                if (occupied.has(`${row + dr},${col + dc}`)) {
                                    fits = false;
                                    break outer;
                                }
                            }
                        }
                        if (fits) return { x: col, y: row };
                    }
                }
                return { x: 0, y: maxUsedRow };
            };

            const sortedMissing = [...missingWidgets].sort(
                (a, b) => a.SystemDashboardWidgetTypeId - b.SystemDashboardWidgetTypeId
            );

            for (const widget of sortedMissing) {
                let w = 6, h = 4;
                if (widget.SystemDashboardWidgetTypeId === 2) { w = 2; h = 2; }
                else if (widget.SystemDashboardWidgetTypeId === 3) { w = 6; h = 2; }
                else if (widget.SystemDashboardWidgetTypeId === 1) { w = 6; h = 2; }
                const occupied = buildOccupancy(finalLayout);
                const maxUsedRow = getMaxRow(finalLayout);
                const { x, y } = findFirstFit(occupied, w, h, maxUsedRow);

                finalLayout.push({
                    i: widget.AccountEmployeeDashboardDetailId.toString(),
                    x, y, w, h,
                });
            }

            const cleanLayout = finalLayout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
            dispatch(updateDashboardLayoutAsync({
                AccountEmployeeDashboardsId: currentDashboardId,
                Layout: JSON.stringify(cleanLayout),
            }));
            setBaseLayout(cleanLayout);
        } else {
            setBaseLayout(cleanedExistingLayout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })));
        }

    }, [dashboardWidgetDetails, currentDashboardId, dispatch]);

    const scaleLayout = (layout: any[], fromCols: number, toCols: number, minW: number) => {
        const scaled = layout.map((item) => ({
            ...item,
            w: Math.min(toCols, Math.max(minW, Math.round(item.w * toCols / fromCols))),
            h: item.h,
        }));

        const sorted = [...scaled].sort((a, b) => a.y - b.y || a.x - b.x);
        const colHeights = new Array(toCols).fill(0);

        return sorted.map((item) => {
            let bestX = 0;
            let bestY = Infinity;

            for (let col = 0; col <= toCols - item.w; col++) {
                const maxH = Math.max(...colHeights.slice(col, col + item.w));
                if (maxH < bestY) {
                    bestY = maxH;
                    bestX = col;
                }
            }

            for (let col = bestX; col < bestX + item.w; col++) {
                colHeights[col] = bestY + item.h;
            }

            return { ...item, x: bestX, y: bestY };
        });
    };

    const responsiveLayouts = useMemo(() => {
        if (!baseLayout.length) return {};

        const lg = baseLayout.map((item) => ({ ...item, static: !isDraggable }));

        const mdScaled = scaleLayout(baseLayout, 12, 6, 1);
        const md = mdScaled.map((item) => ({ ...item, static: !isDraggable }));

        const smScaled = scaleLayout(baseLayout, 12, 4, 2);
        const sm = smScaled.map((item) => ({ ...item, static: !isDraggable }));

        return { lg, md, sm, xs: sm, xxs: sm };
    }, [baseLayout, isDraggable]);

    useEffect(() => {
        if (!dashboardRef.current) return;

        const updateWidth = () => {
            if (dashboardRef.current) {
                setWidth(dashboardRef.current.getBoundingClientRect().width);
            }
        };

        updateWidth();

        const ro = new ResizeObserver(() => updateWidth());
        ro.observe(dashboardRef.current);
        window.addEventListener("resize", updateWidth);

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", updateWidth);
        };
    }, []);

    const handleDragStop = (layout: any) => {
        const cleanLayout = layout.map(({ i, x, y, w, h }: any) => ({ i, x, y, w, h }));
        dispatch(updateDashboardLayoutAsync({
            AccountEmployeeDashboardsId: currentDashboardId,
            Layout: JSON.stringify(cleanLayout),
        }));
        setBaseLayout(cleanLayout);
    };

    const onDeleteClick = (e: any, id: any) => {
        e.stopPropagation();
        if (!isDraggable) return;
        ShowSwalWithConfirmation("Are you sure you want to delete this record?", "")
            .then((result) => {
                if (!result) return;
                dispatch(deleteAccountEmployeeDashboardWidgetAsync(id))
                    .unwrap()
                    .then((res) => {
                        if (res.StatusCode === "200") {
                            setWidgetFilters((prev) => {
                                const updated = { ...prev };
                                delete updated[id];
                                writeFiltersToStorage(currentDashboardId, updated);
                                return updated;
                            });
                            setBaseLayout((prev) => {
                                const updated = prev.filter((item) => item.i !== id.toString());
                                dispatch(updateDashboardLayoutAsync({
                                    AccountEmployeeDashboardsId: currentDashboardId,
                                    Layout: JSON.stringify(updated),
                                }));
                                return updated;
                            });
                            dispatch(getDefaultDashboardWidgetDetailsAsync(currentDashboardId));
                            ShowToast("Success", "Widget Deleted Successfully", "success", "toast-top-right");
                        } else {
                            ShowToast("Error", "Failed to delete item", "error", "toast-top-right");
                        }
                    })
                    .catch(() => { });
            })
            .catch(() => { });
    };

    const handleFilterClick = (e: React.MouseEvent<HTMLElement>, widgetId: string, widget: any) => {
        e.stopPropagation();
        setFilterPopover((prev) =>
            prev?.widgetId === widgetId ? null : { widgetId, anchorEl: e.currentTarget, widget }
        );
        dispatch(GetWidgetFilters(widget?.SystemDashboardWidgetId));
    };

    const handleFilterApply = (filters: any) => {
        const widgetId = filterPopover?.widget.AccountEmployeeDashboardDetailId;
        setWidgetFilters((prev) => ({ ...prev, [widgetId]: filters }));
        dispatch(getWidgetDataAsync({
            AccountEmployeeDashboardDetailId: widgetId,
            filterJson: JSON.stringify(filters),
        }));
    };

    const handleRefreshWidget = (widget: any) => {
        const widgetId = widget.AccountEmployeeDashboardDetailId;
        const savedFilter = widgetFilters[widgetId];
        dispatch(getWidgetDataAsync({
            AccountEmployeeDashboardDetailId: widgetId,
            ...(savedFilter && Object.keys(savedFilter).length > 0
                ? { filterJson: JSON.stringify(savedFilter) }
                : {}),
        }));
    };

    const handleRefreshAllWidgets = () => {
        // setWidgetFilters({});
        if (currentDashboardId) dispatch(getDefaultDashboardWidgetDetailsAsync(currentDashboardId));
    };

    const handlePinNavigate = (dash: any) => {
        if (dash.AccountEmployeeDashboardsId === currentDashboardId) {
            dispatch(updateRefreshState(true));
            return;
        }
        setIsDraggable(false);
        setBaseLayout([]);
        dispatch(clearDashboardWidgets());
        setWidgetFilters(readFiltersFromStorage(dash.AccountEmployeeDashboardsId));
        navigate(`/dashboard/${dash.AccountEmployeeDashboardsId}`, {
            state: { obj: { AccountEmployeeDashboardsId: dash.AccountEmployeeDashboardsId } },
        });
    };

    const getIconStyle = (isActive: boolean, color: string) => ({
        color: color,
        cursor: 'pointer',
        opacity: isActive ? 1.5 : 0.5,
        transition: "all 0.2s ease",
    });

    const handleWidgetNameSave = async (widget: any) => {
        const trimmed = editingName.trim();
        setEditingWidgetId(null);

        if (!trimmed || trimmed === widget.WidgetName) return;

        try {
            const res = await dispatch(renameWidgetAsync({ AccountEmployeeDashboardDetailId: widget.AccountEmployeeDashboardDetailId, WidgetName: trimmed, }));
            if (res?.meta?.requestStatus === 'fulfilled') {
                dispatch(getDefaultDashboardWidgetDetailsAsync(currentDashboardId));
                ShowToast("Success", "Widget renamed successfully", "success", "toast-top-right");
            } else {
                ShowToast("Error", "Failed to rename widget", "error", "toast-top-right");
            }
        } catch {
            ShowToast("Error", "Failed to rename widget", "error", "toast-top-right");
        }
    };

    const handleWidgetNameKeyDown = (e: React.KeyboardEvent, widget: any) => {
        if (e.key === "Enter") handleWidgetNameSave(widget);
        if (e.key === "Escape") setEditingWidgetId(null);
    };

    const handleWidgetNameClick = (e: React.MouseEvent, widget: any) => {
        e.stopPropagation();

        if (!permissions?.IsEdit) return;

        setEditingWidgetId(widget.AccountEmployeeDashboardDetailId);
        setEditingName(widget.WidgetName);

        setTimeout(() => {
            const input = editInputRef.current;
            if (input) {
                input.focus();

                input.setSelectionRange(input.value.length, input.value.length);
            }
        }, 50);
    };

    const handleCancelFilter = () => {
        const widgetId = filterPopover?.widget?.AccountEmployeeDashboardDetailId;
        if (!widgetId) {
            setFilterPopover(null);
            return;
        }
        setWidgetFilters((prev) => {
            const updated = { ...prev };
            delete updated[widgetId];
            writeFiltersToStorage(currentDashboardId, updated);
            return updated;
        });
        dispatch(getWidgetDataAsync({ AccountEmployeeDashboardDetailId: widgetId }));
        setFilterPopover(null);
    };

    return (
        <>
            {isFullPageLoading && <Loader />}
            <div className="card card-custom admin-card">
                <div className="card-header card-drop sticky-top fixed-top bg-white flex-column align-items-start">
                    <div className="d-flex align-items-center gap-2 mt-2 w-100" style={{ borderBottom: "1px solid #eee", paddingBottom: "6px" }}>
                        {pinData?.map((dash: any) => (
                            <div
                                key={dash.AccountEmployeeDashboardsId}
                                className={`d-flex align-items-center px-3 py-1 rounded cursor-pointer ${currentDashboardId === dash.AccountEmployeeDashboardsId
                                    ? "bg-light-primary text-primary"
                                    : ""
                                    }`}
                                onClick={() => handlePinNavigate(dash)}
                                style={{ fontSize: "13px", fontWeight: 500, border: "1px solid #000" }}
                            >
                                <i className="bi bi-pin-angle-fill me-2 fs-7"></i>
                                {dash.DashboardName}
                            </div>
                        ))}
                    </div>

                    <div className="d-flex justify-content-between align-items-center w-100">
                        <h3 className="card-title fw-bold headerTitle">{DashboardTitle}</h3>
                        <div className="card-toolbar align-items-center">
                            <>
                                <div className="form-check form-switch form-check-custom form-check-solid me-0">
                                    <label className="fs-7 fw-normal" style={{ marginRight: 5 }}>
                                        {getValueFromResource("Open In New Tab :", dispatch, t)}
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={OpenInNewTab}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            const newValue = e.target.checked;
                                            //setIsOpenInNewTab(newValue);
                                            dispatch(UpdateStateOpenInNewTab(newValue));
                                            dispatch(updateEmployeeOpenNewTabAction({ openInNewTab: newValue }));
                                        }}
                                        className="form-check-input h-20px w-40px"
                                    />
                                </div>
                                <div className="bullet bg-secondary h-35px w-1px mx-3"></div>
                            </>
                            {permissions?.IsEdit && (
                                <>
                                    <div className="form-check form-switch form-check-custom form-check-solid me-0">
                                        <label className="fs-7 fw-normal" style={{ marginRight: 5 }}>
                                            {getValueFromResource("Edit Mode :", dispatch, t)}
                                        </label>
                                        <input
                                            type="checkbox"
                                            checked={isDraggable}
                                            onChange={(e) => setIsDraggable(e.target.checked)}
                                            className="form-check-input h-20px w-40px"
                                        />
                                    </div>
                                    <div className="bullet bg-secondary h-35px w-1px mx-3"></div>
                                </>
                            )}
                            <a
                                className="btn btn-icon btn-active-light-dark btn-sm ms-0"
                                data-tooltip-id="dashboard-tooltip"
                                data-tooltip-content={getValueFromResource("Refresh All", dispatch, t)}
                                data-tooltip-place="bottom"
                                onClick={handleRefreshAllWidgets}
                            >
                                <i className="bi bi-arrow-repeat fs-3 iconColor"></i>
                            </a>
                            <div className="bullet bg-secondary h-35px w-1px mx-3"></div>

                            <a
                                className="btn btn-icon btn-active-light-dark btn-sm ms-0"
                                data-tooltip-id="dashboard-tooltip"
                                data-tooltip-content={getValueFromResource("Dashboard List", dispatch, t)}
                                data-tooltip-place="bottom"
                                onClick={() => navigate("/dashboardSummary")}
                            >
                                <i className="bi-card-list fs-3 iconColor"></i>
                            </a>
                            <div className="bullet bg-secondary h-35px w-1px mx-2"></div>
                            {/* {((permissions?.IsAdd && dashboardWidgetDetails !== null && !(dashboardWidgetDetails?.length >= 8))) && <button
                                onClick={() => setShowAddWidgetModal(true)}
                                className="btn btn-primary btn-bs-btn-font-weight btn-sm"
                            >
                                {getValueFromResource("+ Add New Widget", dispatch, t)}
                            </button>} */}
                            <span
                                data-tooltip-id="dashboard-tooltip"
                                data-tooltip-content={!permissions?.IsAdd ? getValueFromResource("You don't have permission to add widgets", dispatch, t) : dashboardWidgetDetails?.length >= 8 ? getValueFromResource("Maximum of 8 widgets allowed per dashboard. Remove an existing widget to add a new one", dispatch, t) : ""}
                                data-tooltip-place="bottom"
                                style={{
                                    display: "inline-block",
                                    cursor: !permissions?.IsAdd || dashboardWidgetDetails?.length >= 8 ? "not-allowed" : "default"
                                }}
                            >
                                <button
                                    onClick={() => setShowAddWidgetModal(true)}
                                    disabled={!permissions?.IsAdd || dashboardWidgetDetails === null || dashboardWidgetDetails?.length >= 8}
                                    className="btn btn-primary btn-bs-btn-font-weight btn-sm"
                                    style={{ pointerEvents: !permissions?.IsAdd || dashboardWidgetDetails?.length >= 8 ? "none" : "auto" }}
                                >
                                    {getValueFromResource("+ Add New Widget", dispatch, t)}
                                </button>
                            </span>
                        </div>
                    </div>
                </div>

                <div ref={dashboardRef} style={{ width: "100%", height: "100%", marginBottom: "4%" }}>
                    {!dashboardWidgetDetails || dashboardWidgetDetails.length === 0 ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: "300px", flexDirection: "column" }}>
                            <p className="noDataFound fs-5">
                                {getValueFromResource("No widgets found on this dashboard.", dispatch, t)}
                            </p>
                        </div>
                    ) : !baseLayout.length || !width ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: "200px", color: "#9CA3AF", fontSize: "14px" }}>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            <span>{getValueFromResource("Loading dashboard...", dispatch, t)}</span>
                        </div>
                    ) : (
                        <RGL
                            className="layout"
                            layouts={responsiveLayouts}
                            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                            cols={{ lg: 12, md: 6, sm: 4, xs: 4, xxs: 4 }}
                            rowHeight={90}
                            width={width}
                            compactType="vertical"
                            margin={[15, 15]}
                            containerPadding={[10, 10]}
                            isDraggable={isDraggable}
                            isResizable={isDraggable}
                            draggableHandle=".drag-handle"
                            onDragStop={(layout: any) => handleDragStop(layout)}
                            onResizeStop={(layout: any) => handleDragStop(layout)}
                            onMouseLeave={(e: any) => {
                                const related = e.relatedTarget as Node;
                                const currentTarget = e.currentTarget as Node;
                                if (currentTarget.contains(related)) return;
                                setHoveredWidgetId(null);
                            }}
                        >
                            {dashboardWidgetDetails?.map((widget: any) => {
                                const widgetIdStr = widget.AccountEmployeeDashboardDetailId.toString();
                                const isFilterOpen = filterPopover?.widgetId === widgetIdStr;
                                const isWidgetActive = hoveredWidgetId === widgetIdStr || isFilterOpen;
                                const hasFilter = widget?.HasFilter;
                                const isFeatureEnabled = widget?.IsFeatureEnabled !== false;
                                const hasActiveFilter = !!widgetFilters[widget.AccountEmployeeDashboardDetailId] &&
                                    Object.keys(widgetFilters[widget.AccountEmployeeDashboardDetailId]).length > 0;
                                const systemDashboardWidgetId = [3, 4, 5, 9, 11, 14, 15, 16].includes(widget?.SystemDashboardWidgetId);
                                const canDelete = permissions?.IsDelete;
                                return (
                                    <div
                                        key={widget.AccountEmployeeDashboardDetailId}
                                        style={{
                                            ...widgetStyle,
                                            // opacity: isFeatureEnabled ? 1 : 0.6,
                                            // background: isFeatureEnabled ? "#fff" : "#f3f4f6",
                                             opacity:  1,
                                            background: "#fff",
                                            position: "relative",
                                            cursor: isDraggable ? 'grab' : isFeatureEnabled ? "default" : "not-allowed",
                                            border: "1px solid #e0e0e0",
                                        }}
                                        onMouseEnter={() => setHoveredWidgetId(widgetIdStr)}
                                        onMouseLeave={() => { if (!isFilterOpen) setHoveredWidgetId(null); }}
                                    >
                                        {!isFeatureEnabled && isDraggable && canDelete && (
                                            <button
                                                style={{
                                                    position: "absolute",
                                                    top: 10,
                                                    right: 10,
                                                    zIndex: 20,
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 5,
                                                    padding: "0 10px 0 8px",
                                                    height: 28,
                                                    borderRadius: 6,
                                                    background: "#ffffff",
                                                    color: "#B91C1C",
                                                    cursor: "pointer",
                                                    transition: "all 0.2s ease",
                                                }}
                                                onMouseEnter={(e) => {
                                                    const el = e.currentTarget as HTMLButtonElement;
                                                    el.style.background = "#DC2626";
                                                    el.style.color = "#ffffff";
                                                    // el.style.borderColor = "#DC2626";
                                                    const icon = el.querySelector('i') as HTMLElement;
                                                    if (icon) icon.style.color = "#ffffff";
                                                }}
                                                onMouseLeave={(e) => {
                                                    const el = e.currentTarget as HTMLButtonElement;
                                                    el.style.background = "#ffffff";
                                                    el.style.color = "#dc3545";
                                                    const icon = el.querySelector('i') as HTMLElement;
                                                    if (icon) icon.style.color = "#dc3545";
                                                }}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => onDeleteClick(e, widget.AccountEmployeeDashboardDetailId)}
                                            >
                                                <i className="bi bi-trash fs-6" style={{ ...getIconStyle(true, "#dc3545") }} />
                                            </button>
                                        )}
                                        {!isFeatureEnabled && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    zIndex: 10,
                                                    borderRadius: "12px",
                                                    background: "rgba(17, 24, 39, 0.9)",
                                                    backdropFilter: "blur(16px)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    padding: "14px",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        textAlign: "center",
                                                        color: "#fff",
                                                        maxWidth: "220px",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "40px",
                                                            height: "40px",
                                                            borderRadius: "10px",
                                                            background: "rgba(255,255,255,0.12)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            margin: "0 auto 10px",
                                                        }}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path
                                                                d="M5 7V5a3 3 0 0 1 6 0v2"
                                                                stroke="#fff"
                                                                strokeWidth="1.6"
                                                                strokeLinecap="round"
                                                            />
                                                            <rect
                                                                x="3"
                                                                y="7"
                                                                width="10"
                                                                height="8"
                                                                rx="2"
                                                                stroke="#fff"
                                                                strokeWidth="1.6"
                                                            />
                                                        </svg>
                                                    </div>

                                                    <div
                                                        style={{
                                                            fontSize: "11.5px",
                                                            opacity: 1.5,
                                                            lineHeight: "1.4",
                                                            marginBottom: "12px",
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        {getValueFromResource(`${widget?.FeatureName} is not enabled in your Subscription. Contact your administrator or sales@livetecs.com.`, dispatch, t)}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ marginBottom: 6 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
                                                <div className="drag-handle" style={{ cursor: isDraggable ? "grab" : 'default', display: "flex", alignItems: "center", paddingRight: "6px", flex: 1, minWidth: 0, gap: 2 }}>
                                                    {isDraggable && <i className="fa fa-grip-vertical six-dots-icon" style={{ fontSize: "12px", color: "#6B7280", marginRight: '3px', opacity: 0.7 }}></i>}
                                                    {(isDraggable && editingWidgetId === widget.AccountEmployeeDashboardDetailId) ? (
                                                        <input
                                                            ref={editInputRef}
                                                            value={editingName}
                                                            onChange={(e) => setEditingName(e.target.value)}
                                                            onKeyDown={(e) => handleWidgetNameKeyDown(e, widget)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onFocus={() => setIsInputFocused(true)}
                                                            onBlur={() => {
                                                                setIsInputFocused(false);
                                                                handleWidgetNameSave(widget);
                                                            }}
                                                            style={{
                                                                fontSize: 14,
                                                                fontWeight: 600,
                                                                color: "#111827",
                                                                border: isInputFocused ? "1px solid #eee" : "1px solid transparent",
                                                                background: isInputFocused ? "#F9FAFB" : "transparent",
                                                                boxShadow: isInputFocused ? "0 0 0 2px rgba(59,130,246,0.15)" : "none",
                                                                flex: 1,
                                                                minWidth: 0,
                                                                padding: "2px 6px",
                                                                margin: 0,
                                                                width: "100%",
                                                                caretColor: "#111827",
                                                                borderRadius: "4px",
                                                                transition: "all 0.2s ease",
                                                            }}
                                                        />
                                                    ) : (
                                                        <h4
                                                            style={{
                                                                ...headerStyle,
                                                                whiteSpace: "normal",
                                                                lineHeight: 1,
                                                                textOverflow: "ellipsis",
                                                                flex: 1,
                                                                minWidth: 0,
                                                                marginBottom: 0,
                                                                cursor: permissions?.IsEdit ? "text" : "default",
                                                                userSelect: "none",
                                                            }}
                                                            onClick={(e) => handleWidgetNameClick(e, widget)}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            title={permissions?.IsEdit ? "Double-click to rename" : widget.WidgetName}
                                                        >
                                                            {getValueFromResource(`${(widget.SystemDashboardWidgetId === 8 ? widget.WidgetName + `- (${selectedYear})` : widget.WidgetName)}`, dispatch, t)}
                                                        </h4>
                                                    )}
                                                </div>

                                                {isFeatureEnabled && (
                                                    <div style={{
                                                        display: "flex",
                                                        gap: 6,
                                                        alignItems: "center",
                                                        flexShrink: 0,
                                                        flexWrap: "nowrap",
                                                        lineHeight: 1,
                                                    }}>
                                                        {!isDraggable && systemDashboardWidgetId && (
                                                            // <i
                                                            //     className="bi bi-box-arrow-up-right fs-5 mb-1"
                                                            //     style={getIconStyle(isWidgetActive, "#000")}
                                                            //     data-tooltip-id="dashboard-tooltip"
                                                            //     data-tooltip-content={getValueFromResource("View Detail", dispatch, t)}
                                                            //     onClick={() => {
                                                            //         if (isWidgetActive && widget?.IsDrillDown) {
                                                            //             const data = {
                                                            //                 widgetFilters: widgetFilters[widget.AccountEmployeeDashboardDetailId] || null,
                                                            //                 fromDashboard: true,
                                                            //                 status: widgetFilters[widget.AccountEmployeeDashboardDetailId]?.AccountStatusId || null
                                                            //             };
                                                            //             const encoded = encodeURIComponent(JSON.stringify(data));
                                                            //             const url = `${widget?.DrillDownRoute}?data=${encoded}`;
                                                            //             window.open(url, "_blank");

                                                            //         }
                                                            //     }}
                                                            // />
                                                            <i
                                                                className="bi bi-box-arrow-up-right fs-5 mb-1"
                                                                style={getIconStyle(isWidgetActive, "#000")}
                                                                data-tooltip-id="dashboard-tooltip"
                                                                data-tooltip-content={getValueFromResource("View Detail", dispatch, t)}
                                                                onClick={() => {
                                                                    if (isWidgetActive && widget?.IsDrillDown) {
                                                                        const data = {
                                                                            widgetFilters: widgetFilters[widget.AccountEmployeeDashboardDetailId] || null,
                                                                            fromDashboard: true,
                                                                            status: widgetFilters[widget.AccountEmployeeDashboardDetailId]?.AccountStatusId || null
                                                                        };
                                                                        const encoded = encodeURIComponent(JSON.stringify(data));
                                                                        const targetUrl = `${widget?.DrillDownRoute}?data=${encoded}`;
                                                                        if (OpenInNewTab) {
                                                                            window.open(targetUrl, "_blank");
                                                                        } else {
                                                                            localStorage.setItem('returnToDashboard', window.location.pathname + window.location.search);
                                                                            navigate(targetUrl);
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        )}

                                                        {!isDraggable && hasFilter && (
                                                            <span style={{ position: "relative", display: "inline-flex" }}>
                                                                <i
                                                                    className="bi bi-filter fs-5"
                                                                    style={getIconStyle(isWidgetActive, "#000")}
                                                                    data-tooltip-id="dashboard-tooltip"
                                                                     onMouseDown={(e) => e.stopPropagation()}
                                                                    data-tooltip-content={getValueFromResource("Filter", dispatch, t)}
                                                                    onClick={(e) => handleFilterClick(e, widgetIdStr, widget)}
                                                                />
                                                                {hasActiveFilter && (
                                                                    <span style={{ position: "absolute", top: -2, right: -2, width: 7, height: 7, borderRadius: "50%", background: "#2563EB", border: "1px solid #fff" }} />
                                                                )}
                                                            </span>
                                                        )}

                                                        {!isDraggable && <i
                                                            className="bi bi-arrow-repeat fs-6"
                                                            style={getIconStyle(isWidgetActive, "#000")}
                                                            data-tooltip-id="dashboard-tooltip"
                                                             onMouseDown={(e) => e.stopPropagation()}
                                                            data-tooltip-content={getValueFromResource("Refresh", dispatch, t)}
                                                            onClick={() => handleRefreshWidget(widget)}
                                                        />}
                                                        {(isDraggable && permissions?.IsDelete) && <i
                                                            className="bi bi-trash fs-6"
                                                            style={{
                                                                ...getIconStyle(canDelete, "#dc3545"),
                                                                // pointerEvents: permissions?.IsDelete ? "auto" : "none",
                                                            }}
                                                            data-tooltip-id="dashboard-tooltip"
                                                            data-tooltip-content={getValueFromResource("Delete", dispatch, t)}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onClick={(e) => {
                                                                if (!canDelete) return;
                                                                onDeleteClick(e, widget.AccountEmployeeDashboardDetailId);
                                                            }}
                                                        />}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <DynamicWidget
                                            widget={widget}
                                            isDraggable={isDraggable}
                                            widgetsResponseById={widgetsResponseById}
                                            appliedFilters={widgetFilters[widget.AccountEmployeeDashboardDetailId] || null}
                                        />
                                    </div>
                                );
                            })}
                        </RGL>
                    )}
                </div>

                {showAddWidgetModal && (
                    <AddWidgetsModal
                        onClose={() => {
                            setShowAddWidgetModal(false);
                            dispatch(getDefaultDashboardWidgetDetailsAsync(currentDashboardId));
                        }}
                    />
                )}
            </div>

            {filterPopover && (
                <WidgetFilterPopover
                    anchorEl={filterPopover.anchorEl}
                    onClose={() => setFilterPopover(null)}
                    onCancel={handleCancelFilter}
                    onApply={handleFilterApply}
                    savedFilters={widgetFilters[filterPopover.widget.AccountEmployeeDashboardDetailId] || null}
                    expectedWidgetId={filterPopover.widget.SystemDashboardWidgetId}
                />
            )}

            <ReactTooltip
                id="dashboard-tooltip"
                place="top"
                style={{ fontSize: "12px", fontWeight: "normal", padding: "8px 12px", borderRadius: "6px", backgroundColor: "#1F2937", color: "white", zIndex: 999999, maxWidth: "250px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
            />
        </>
    );
};

export default DashboardView;